import React, { createContext, useContext, useState, useRef, useCallback, ReactNode, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { logout } from '../utils/authUtils';
import { setSessionExpiredCallback } from '../utils/apiUtils';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface SessionContextType {
  showSessionExpiredModal: boolean;
  triggerSessionExpired: () => void;
  logoutSecondsLeft: number;
  setSaveDraftCallback: (callback: (() => Promise<void>) | null) => void;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

interface SessionProviderProps {
  children: ReactNode;
}

export const SessionProvider: React.FC<SessionProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState<boolean>(false);
  const [logoutSecondsLeft, setLogoutSecondsLeft] = useState<number>(10);
  const [isExtending, setIsExtending] = useState<boolean>(false);
  const [isSavingDraft, setIsSavingDraft] = useState<boolean>(false);
  const [draftSaved, setDraftSaved] = useState<boolean>(false);
  const logoutTimerRef = useRef<NodeJS.Timeout | null>(null);
  const saveDraftCallbackRef = useRef<(() => Promise<void>) | null>(null);

  const clearAutoLogout = useCallback(() => {
    if (logoutTimerRef.current) {
      clearInterval(logoutTimerRef.current as unknown as number);
      logoutTimerRef.current = null;
    }
  }, []);

  // Hide modal when navigating to login page
  useEffect(() => {
    if (location.pathname === '/' || location.pathname === '/login') {
      setShowSessionExpiredModal(false);
      clearAutoLogout();
    }
  }, [location.pathname, clearAutoLogout]);

  const setSaveDraftCallback = useCallback((callback: (() => Promise<void>) | null) => {
    saveDraftCallbackRef.current = callback;
  }, []);

  const forceLogout = useCallback(async () => {
    try {
      clearAutoLogout();
      
      // Save draft before logging out if callback is available
      if (saveDraftCallbackRef.current && !draftSaved) {
        setIsSavingDraft(true);
        try {
          await saveDraftCallbackRef.current();
          setDraftSaved(true);
        } catch (e) {
          console.error('Error saving draft before logout:', e);
        }
        setIsSavingDraft(false);
      }
      
      sessionStorage.removeItem('userProfileData');
      sessionStorage.removeItem('isLoggedIn');
      await logout();
    } catch (e) {
      // swallow
    } finally {
      setShowSessionExpiredModal(false);
      setDraftSaved(false);
      navigate('/');
    }
  }, [clearAutoLogout, navigate, draftSaved]);

  const extendSession = useCallback(async () => {
    setIsExtending(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/extend-session`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        clearAutoLogout();
        setShowSessionExpiredModal(false);
        setDraftSaved(false);
      } else {
        // If extend fails, proceed with logout
        forceLogout();
      }
    } catch (error) {
      console.error('Error extending session:', error);
      forceLogout();
    } finally {
      setIsExtending(false);
    }
  }, [clearAutoLogout, forceLogout]);

  const beginAutoLogoutCountdown = useCallback(async () => {
    clearAutoLogout();
    setLogoutSecondsLeft(10);
    setDraftSaved(false);
    
    // Auto-save draft when session expires modal appears
    if (saveDraftCallbackRef.current) {
      setIsSavingDraft(true);
      try {
        await saveDraftCallbackRef.current();
        setDraftSaved(true);
      } catch (e) {
        console.error('Error auto-saving draft:', e);
      }
      setIsSavingDraft(false);
    }
    
    const id = setInterval(() => {
      setLogoutSecondsLeft(prev => {
        if (prev <= 1) {
          clearAutoLogout();
          forceLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    logoutTimerRef.current = id as unknown as NodeJS.Timeout;
  }, [clearAutoLogout, forceLogout]);

  const triggerSessionExpired = useCallback(() => {
    // Only show session expired modal if user was actually logged in
    // Check for session indicators that are set during successful login
    const hasUserProfile = sessionStorage.getItem('userProfileData');
    const hasApplicationId = sessionStorage.getItem('currentApplicationId');
    const isLoggedIn = sessionStorage.getItem('isLoggedIn') === 'true';
    
    // User must have logged in (has profile data or application in progress or explicit login flag)
    const hasActiveSession = hasUserProfile || (hasApplicationId && isLoggedIn);
    
    if (!showSessionExpiredModal && hasActiveSession) {
      setShowSessionExpiredModal(true);
      beginAutoLogoutCountdown();
    }
  }, [showSessionExpiredModal, beginAutoLogoutCountdown]);

  // Register the callback with apiUtils so API errors can trigger session expiry
  useEffect(() => {
    setSessionExpiredCallback(triggerSessionExpired);
  }, [triggerSessionExpired]);

  return (
    <SessionContext.Provider value={{ showSessionExpiredModal, triggerSessionExpired, logoutSecondsLeft, setSaveDraftCallback }}>
      {children}
      {showSessionExpiredModal && (
        <ModalOverlay>
          <ModalContent
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <ModalHeader>Session expired</ModalHeader>
            <ModalBody>
              <p>Your login session has expired. For your security, you will be logged out automatically in <strong>{logoutSecondsLeft}</strong> seconds.</p>
              {isSavingDraft && (
                <SaveStatus style={{ color: '#f39c12' }}>
                  ⏳ Saving your application...
                </SaveStatus>
              )}
              {draftSaved && !isSavingDraft && (
                <SaveStatus style={{ color: '#27ae60' }}>
                  ✓ Your application has been saved. You can resume from where you left off after logging back in.
                </SaveStatus>
              )}
              <p>Please log in again to continue.</p>
            </ModalBody>
            <ModalFooter>
              <ExtendButton onClick={extendSession} disabled={isExtending}>
                {isExtending ? 'Extending...' : 'Extend Session'}
              </ExtendButton>
              <ModalButton onClick={forceLogout}>
                Logout now
              </ModalButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
    </SessionContext.Provider>
  );
};

// Styled Components for Session Modal
const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const ModalContent = styled(motion.div)`
  background: white;
  padding: 25px;
  border-radius: 12px;
  max-width: 450px;
  width: 90%;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
`;

const ModalHeader = styled.h2`
  margin: 0 0 15px 0;
  color: #2c3e50;
  font-size: 1.4rem;
`;

const ModalBody = styled.div`
  margin-bottom: 20px;
  color: #555;
  line-height: 1.6;
  
  p {
    margin: 0 0 10px 0;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const ModalButton = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s;
  background-color: #e74c3c;
  color: white;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }
`;

const ExtendButton = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s;
  background-color: #27ae60;
  color: white;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    background-color: #95a5a6;
    cursor: not-allowed;
  }
`;

const SaveStatus = styled.div`
  padding: 10px;
  margin: 10px 0;
  border-radius: 6px;
  background-color: #f8f9fa;
  font-size: 0.9em;
  line-height: 1.5;
`;

export default SessionContext;
